export type SettingsAudience = 'personal' | 'member' | 'admin' | 'owner';
export type SettingsApp = 'mail' | 'calendar' | 'tasks' | 'notes' | 'contacts' | 'files';

export type SettingsPage =
  | 'account'
  | 'time-date'
  | 'notifications'
  | 'usage'
  | 'security'
  | 'billing-personal'
  | 'payment-methods-personal'
  | 'shared-items'
  | 'shared-workspace'
  | 'team-members'
  | 'team-usage'
  | 'billing-team'
  | 'payment-methods-team'
  | 'workspace'
  | 'mail-permissions'
  | 'mail-import'
  | 'mail-signatures'
  | 'mail-auto-reply'
  | 'mail-templates'
  | 'team-blocklist'
  | 'calendar'
  | 'tasks';

export interface SettingsMenuRow {
  readonly id: SettingsPage | SettingsApp;
  readonly label: string;
  readonly path: string;
  readonly kind: 'page' | 'app';
  readonly icon?: string;
  readonly children?: readonly SettingsMenuRow[];
}

export interface SettingsMenuGroup {
  readonly label: 'Account' | 'Team workspace' | 'Apps';
  readonly rows: readonly SettingsMenuRow[];
}

export type PageExists = (page: SettingsPage) => boolean;
export type CanSeeApp = (app: SettingsApp) => boolean;

export const SHIPPED_SETTINGS_PAGES = new Set<SettingsPage>([
  'account',
  'time-date',
  'notifications',
  'usage',
  'security',
  'shared-items',
  'shared-workspace',
  'team-members',
  'team-usage',
  'billing-personal',
  'billing-team',
  'payment-methods-personal',
  'payment-methods-team',
  'workspace',
  'mail-permissions',
  'mail-import',
  'mail-signatures',
  'mail-auto-reply',
  'mail-templates',
  'calendar',
  'tasks',
]);

/**
 * `teamLogin` is the login's kind, not the audience: Notifications is Chat's notification page, and a
 * login that is not known to be a team one has no Chat to be notified by.
 */
export function shippedSettingsPages(audience: SettingsAudience, teamLogin: boolean): PageExists {
  return (page) =>
    SHIPPED_SETTINGS_PAGES.has(page) &&
    (page !== 'team-usage' || audience === 'owner' || audience === 'admin') &&
    (page !== 'notifications' || teamLogin);
}

function flattenMenuRows(rows: readonly SettingsMenuRow[]): readonly SettingsMenuRow[] {
  return rows.flatMap((row) => [row, ...flattenMenuRows(row.children ?? [])]);
}

/** Every shipped menu path visible to one audience, including app children. */
export function settingsVisiblePaths(
  audience: SettingsAudience,
  canSeeApp: CanSeeApp,
  teamLogin: boolean,
): ReadonlySet<string> {
  return new Set(
    settingsMenu(audience, shippedSettingsPages(audience, teamLogin), canSeeApp)
      .flatMap(({ rows }) => flattenMenuRows(rows))
      .map(({ path }) => path),
  );
}

/** Whether the same rule that renders the menu permits opening this settings path. */
export function settingsPageAllowed(
  path: string,
  audience: SettingsAudience,
  canSeeApp: CanSeeApp,
  teamLogin: boolean,
): boolean {
  return settingsVisiblePaths(audience, canSeeApp, teamLogin).has(path);
}

const page = (id: SettingsPage, label: string, path: string): SettingsMenuRow => ({ id, label, path, kind: 'page' });

const mailChildren = (audience: SettingsAudience): readonly SettingsMenuRow[] => [
  page('mail-permissions', 'Email Permissions', 'mail/permissions'),
  page('mail-import', 'Import', 'mail/import'),
  page('mail-signatures', 'Signatures', 'mail/signatures'),
  page('mail-auto-reply', 'Auto Reply', 'mail/auto-reply'),
  page('mail-templates', 'Templates', 'mail/templates'),
  ...(audience === 'owner' ? [page('team-blocklist', 'Team blocklist', 'mail/team-blocklist')] : []),
];

const app = (
  id: SettingsApp,
  label: string,
  path: string,
  icon: string,
  children?: readonly SettingsMenuRow[],
): SettingsMenuRow => ({ id, label, path, icon, kind: 'app', children });

export function settingsMenu(
  audience: SettingsAudience,
  pageExists: PageExists,
  canSeeApp: CanSeeApp,
): readonly SettingsMenuGroup[] {
  const accountRows: readonly SettingsMenuRow[] = [
    page('account', 'Account', 'general/account'),
    page('time-date', 'Time & Date', 'general/time'),
    page('notifications', 'Notifications', 'general/chat-notifications'),
    page('usage', 'Usage', 'general/usage'),
    page('security', 'Security', 'general/security'),
    ...(audience === 'personal'
      ? [
          page('shared-items', 'Shared items', 'general/shared-items'),
          page('billing-personal', 'Billing', 'general/billing'),
          page('payment-methods-personal', 'Payment Methods', 'general/payment-methods'),
        ]
      : [page('shared-workspace', 'Shared workspace', 'general/shared-workspace')]),
  ];

  const teamRows: readonly SettingsMenuRow[] =
    audience === 'admin'
      ? [page('team-members', 'Team members', 'team/people'), page('team-usage', 'Team usage', 'team/usage')]
      : audience === 'owner'
        ? [
            page('team-members', 'Team members', 'team/people'),
            page('team-usage', 'Team usage', 'team/usage'),
            page('billing-team', 'Billing', 'team/billing'),
            page('payment-methods-team', 'Payment Methods', 'team/payment-methods'),
            page('workspace', 'Workspace', 'team/workspace'),
          ]
        : [];

  const apps: readonly SettingsMenuRow[] = [
    app('mail', 'Mail', 'mail', 'sapphire-email', mailChildren(audience)),
    app('calendar', 'Calendar', 'calendar', 'sapphire-calendars'),
    app('contacts', 'Contacts', 'contacts', 'sapphire-contact'),
    app('tasks', 'Tasks', 'tasks', 'sapphire-tasks'),
    app('notes', 'Notes', 'notes', 'sapphire-notebook'),
    app('files', 'Files', 'files', 'sapphire-file'),
  ];

  const groups: SettingsMenuGroup[] = [
    { label: 'Account', rows: accountRows.filter((row) => pageExists(row.id as SettingsPage)) },
    ...(teamRows.length > 0
      ? [{ label: 'Team workspace' as const, rows: teamRows.filter((row) => pageExists(row.id as SettingsPage)) }]
      : []),
    {
      label: 'Apps',
      rows: apps
        .filter((row) => canSeeApp(row.id as SettingsApp))
        .map((row) => ({
          ...row,
          children: row.children?.filter((child) => pageExists(child.id as SettingsPage)),
        }))
        .filter((row) => (row.children?.length ?? 0) > 0 || pageExists(row.id as SettingsPage)),
    },
  ];
  return groups.filter((group) => group.rows.length > 0);
}
